import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2, Paperclip, CalendarClock, MapPin } from 'lucide-react';
import { moduleApi } from '../../lib/api';

const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const fmtDay = (d: string) => d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
const minutesToEndTime = (start: string, durationMinutes: number) => {
    if (!start || !durationMinutes) return '';
    const [h, m] = start.split(':').map(Number);
    const total = (h || 0) * 60 + (m || 0) + Math.round(durationMinutes);
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    completed: { label: 'Terminé', className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    responded: { label: 'Paiement en attente', className: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
    pending: { label: 'En attente de réponse', className: 'bg-surface-soft text-content-muted border-border' },
};

// Page dédiée à l'historique complet des Démarches client d'un contact CRM — extrait de la fiche contact
// (qui n'affichait qu'un résumé peu détaillé, avec un bug d'affichage sur les choix) pour ne pas surcharger
// cette dernière, avec le détail complet demandé : dates d'envoi/réponse, statut, prestations choisies avec
// prix, lieu et horaires du rendez-vous éventuel.
export const CRMContactDemarchesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [contact, setContact] = React.useState<any | null>(null);
    const [requests, setRequests] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (!id) return;
        Promise.all([moduleApi.getCRMContact(id), moduleApi.getContactDocumentRequests(id)])
            .then(([c, r]: any[]) => {
                setContact(c);
                setRequests(Array.isArray(r) ? r : []);
            })
            .catch(() => { setContact(null); setRequests([]); })
            .finally(() => setLoading(false));
    }, [id]);

    const contactLabel = contact
        ? (contact.type === 'PROFESSIONNEL' ? contact.company_name : `${contact.first_name || ''} ${contact.last_name || ''}`.trim())
        : '';

    return (
        <div className="space-y-6">
            <header className="flex items-center gap-3">
                <button onClick={() => navigate(`/crm/contacts/${id}`)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all">
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-content flex items-center gap-2"><FileText size={22} /> Historique des démarches</h1>
                    <p className="text-content-muted mt-1">{contactLabel || '...'}</p>
                </div>
            </header>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : requests.length === 0 ? (
                <p className="text-center text-content-muted py-12 bg-surface rounded-2xl border border-dashed border-border">Aucune Démarche envoyée à ce contact.</p>
            ) : (
                // Liste de démarches envoyées : élargie comme les autres listes (Emeni, 24/08).
                <div className="space-y-4">
                    {/* Pas de largeur maximale : cette page présente une LISTE, pas un formulaire — elle gagne à occuper l'écran d'un ordinateur. Les écrans de réglages gardent leur plafond : un formulaire étalé sur 1900 px devient illisible. Demande d'Emeni (2026-08-25). */}
                    {requests.map((r: any) => {
                        const status = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
                        const choicesTotal = (r.response_choices || []).reduce((sum: number, c: any) => sum + (Number(c.price_ttc) || 0) * (Number(c.qty) || 1), 0);
                        const kmFee = Number(r.km_fee_amount) || 0;
                        const grandTotal = choicesTotal + kmFee;
                        return (
                            <div key={r.id} className="bg-surface rounded-2xl border border-border p-5 space-y-3">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <p className="text-content font-bold text-base">{r.template_name || 'Démarche'}</p>
                                    <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-lg border ${status.className}`}>{status.label}</span>
                                </div>

                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-content-muted">
                                    <span>Envoyé le {fmtDate(r.sent_at)}</span>
                                    {r.responded_at && <span>Répondu le {fmtDate(r.responded_at)}</span>}
                                    {r.amount_due > 0 && <span>Montant : {fmt(r.amount_due)} — {r.payment_status === 'paid' ? 'réglé' : 'en attente'}</span>}
                                    {r.paid_at && <span>Payé le {fmtDate(r.paid_at)}</span>}
                                </div>

                                {(r.response_choices?.length > 0 || kmFee > 0) && (
                                    <div className="pt-1 space-y-1">
                                        <p className="text-[10px] font-black uppercase tracking-wider text-content-muted">Prestations choisies</p>
                                        {r.response_choices.map((c: any, i: number) => (
                                            <div key={i} className="flex items-center justify-between text-sm text-content">
                                                <span>{c.label}{c.qty > 1 ? ` × ${c.qty}` : ''}</span>
                                                <span className="font-bold">{fmt((Number(c.price_ttc) || 0) * (Number(c.qty) || 1))}</span>
                                            </div>
                                        ))}
                                        {kmFee > 0 && (
                                            <div className="flex items-center justify-between text-sm text-content">
                                                <span>Frais kilométriques</span>
                                                <span className="font-bold">{fmt(kmFee)}</span>
                                            </div>
                                        )}
                                        {grandTotal > 0 && (
                                            <div className="flex items-center justify-between text-sm font-bold text-content pt-1 border-t border-border">
                                                <span>Total</span><span>{fmt(grandTotal)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {r.response_text && <p className="text-sm text-content">{r.response_text}</p>}

                                {(r.custom_answers || []).length > 0 && (
                                    <div className="space-y-1">
                                        {r.custom_answers.map((a: any) => (
                                            <p key={a.id} className="text-xs text-content-muted"><span className="text-content font-semibold">{a.question}</span> — {a.answer}</p>
                                        ))}
                                    </div>
                                )}

                                {r.rdv && (
                                    <div className="pt-2 border-t border-border space-y-1">
                                        <p className="text-[10px] font-black uppercase tracking-wider text-content-muted flex items-center gap-1.5"><CalendarClock size={12} /> Rendez-vous</p>
                                        <p className="text-sm text-content">
                                            {fmtDay(r.rdv.date)} — {r.rdv.start_time}
                                            {r.rdv.end_time ? ` à ${r.rdv.end_time}` : r.rdv.duration_minutes ? ` à ${minutesToEndTime(r.rdv.start_time, r.rdv.duration_minutes)}` : ''}
                                        </p>
                                        {r.rdv.location_type && (
                                            <p className="text-xs text-content-muted flex items-center gap-1.5"><MapPin size={11} /> {r.rdv.location_type === 'domicile' ? 'Chez le client' : "Dans l'établissement"}</p>
                                        )}
                                        {!!r.rdv.km_fee_amount && r.rdv.km_fee_amount > 0 && (
                                            <p className="text-xs text-content-muted">Frais kilométriques (réglés en ligne) : <span className="font-bold text-content">{fmt(r.rdv.km_fee_amount)}</span></p>
                                        )}
                                    </div>
                                )}
                                {!r.rdv && r.status !== 'pending' && (
                                    <p className="pt-2 border-t border-border text-xs text-amber-500 font-semibold">Aucun rendez-vous rattaché à cette démarche.</p>
                                )}

                                {(r.response_attachments || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {r.response_attachments.map((f: any, i: number) => (
                                            <a key={i} href={`/api/document-requests/${r.id}/response-attachment/${i}`} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                                <Paperclip size={11} /> {f.name}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CRMContactDemarchesPage;
